import React, { useRef, useState } from "react";
import { brl, cleanItemName } from "@/api";
import { Button } from "@/components/ui/button";
import { Printer, X, Share2, FileDown, ImageDown, Receipt as ReceiptIcon, Minimize2 } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { toast } from "sonner";
import ThermalReceipt from "@/components/ThermalReceipt";
import { measureBlocks, planPages, sliceCanvas } from "@/utils/paginateReceipt";

// ─── Guardas de rasterizacao (iOS Safari) ──────────────────────────────
// O Safari do iPhone/iPad tem um teto de area/memoria de canvas. Quando o
// recibo e longo (varias paginas A4) e capturado com scale 2, o canvas passa
// do teto: o iOS ZERA o backing store (fica todo branco) mas MANTEM width/
// height validos. O html2canvas devolve "sucesso", a paginacao conta as
// paginas certas e o jsPDF salva N paginas TODAS EM BRANCO. Print nativo
// (printAsHtml) nao passa por canvas, por isso continua correto.
const MAX_CANVAS_PX = 8000000;   // area total segura p/ iPhone Safari
const MAX_CANVAS_DIM = 8192;     // dimensao maxima por lado

// Maior scale (<= desejado) que caiba no orcamento de canvas do dispositivo.
function safeCaptureScale(cssWidth, cssHeight, desired = 2) {
  if (!cssWidth || !cssHeight) return desired;
  let scale = desired;
  while (scale > 0.75) {
    const w = cssWidth * scale, h = cssHeight * scale;
    if (w * h <= MAX_CANVAS_PX && w <= MAX_CANVAS_DIM && h <= MAX_CANVAS_DIM) return scale;
    scale -= 0.25;
  }
  return 0.75;
}

// Amostra uma grade de pixels: true = canvas 100% branco/transparente.
// Usado para detectar o canvas zerado pelo iOS ANTES de gerar o PDF.
function isCanvasBlank(cv) {
  try {
    if (!cv || !cv.width || !cv.height) return true;
    const ctx = cv.getContext("2d");
    if (!ctx) return true;
    const cols = 12, rows = 40;
    for (let i = 0; i < cols; i++) {
      for (let j = 0; j < rows; j++) {
        const x = Math.min(cv.width - 1, Math.floor((i + 0.5) * cv.width / cols));
        const y = Math.min(cv.height - 1, Math.floor((j + 0.5) * cv.height / rows));
        const d = ctx.getImageData(x, y, 1, 1).data;
        if (d[3] > 8 && (d[0] < 245 || d[1] < 245 || d[2] < 245)) return false;
      }
    }
    return true;
  } catch (e) {
    return false; // se nao da pra inspecionar, nao bloqueia o fluxo
  }
}

// Corta as linhas totalmente brancas do FIM do canvas, para a paginacao nao
// emitir paginas finais praticamente vazias.
function trimTrailingWhite(cv) {
  try {
    const ctx = cv.getContext("2d");
    if (!ctx) return cv.height;
    const step = Math.max(1, Math.floor(cv.height / 400));
    for (let y = cv.height - 1; y > 0; y -= step) {
      const row = ctx.getImageData(0, y, cv.width, 1).data;
      for (let x = 0; x < row.length; x += 4) {
        if (row[x + 3] > 8 && (row[x] < 245 || row[x + 1] < 245 || row[x + 2] < 245)) {
          return Math.min(cv.height, y + step + 2);
        }
      }
    }
    return cv.height;
  } catch (e) {
    return cv.height;
  }
}

// Libera o backing store de um canvas descartado (critico no iOS).
function releaseCanvas(cv) {
  try { if (cv) { cv.width = 0; cv.height = 0; } } catch (e) {}
}

export default function Receipt({ sale, store, onClose, wideLayout = false }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [thermalOpen, setThermalOpen] = useState(false);
  // Modo compacto: false = automático (tenta normal → fallback compacto)
  // true = força compacto sempre (mais rápido para recibos sabidamente grandes).
  const [forceCompact, setForceCompact] = useState(false);
  const orderNum = String(sale.order_number).padStart(6, "0");

  // Captura a imagem do recibo. Quando `compact` é true, aplica `.receipt-compact`
  // no clone offscreen antes do html2canvas — reduz padding/margem/fonte.
  // Retorna { canvas, blocks, domHeight }.
  const captureCanvas = async ({ compact = false } = {}) => {
    if (!ref.current) return null;
    try { if (document.fonts && document.fonts.ready) await document.fonts.ready; } catch {}
    await new Promise(r => setTimeout(r, 150));
    const htmlEl = document.documentElement;
    const wasDark = htmlEl.classList.contains("dark");
    if (wasDark) htmlEl.classList.remove("dark");

    const clone = ref.current.cloneNode(true);
    clone.classList.add("receipt-capturing");
    if (compact) clone.classList.add("receipt-compact");
    if (wideLayout) clone.classList.add("receipt-wide");
    const wrapper = document.createElement("div");
    wrapper.style.cssText = "position:fixed;left:-99999px;top:0;background:#ffffff;z-index:-1;pointer-events:none;";
    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    try {
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
      // Mede blocos ANTES de capturar (o html2canvas remove o offscreen depois)
      const blocks = measureBlocks(clone);
      const captureScale = safeCaptureScale(clone.scrollWidth, clone.scrollHeight, 2);
      const canvas = await html2canvas(clone, {
        scale: captureScale,
        backgroundColor: "#ffffff",
        useCORS: true,
        // letterRendering: true (padrão antigo) causa colapso de espaços entre
        // palavras em containers flex/table no html2canvas 1.4.1, gerando texto
        // como "ExpertStoreBR" no PNG rasterizado. Desativar deixa o browser
        // usar fillText nativo com métricas corretas de espaço.
        letterRendering: false,
        logging: false,
        windowWidth: clone.scrollWidth,
        windowHeight: clone.scrollHeight,
      });
      return { canvas, blocks, domHeight: clone.scrollHeight };
    } finally {
      try { document.body.removeChild(wrapper); } catch {}
      if (wasDark) htmlEl.classList.add("dark");
    }
  };

  // Compatibilidade: helpers que esperam só o canvas
  const captureOnlyCanvas = async () => {
    const r = await captureCanvas({ compact: forceCompact });
    return r ? r.canvas : null;
  };

  // Sistema inteligente: escolhe automaticamente a versão que rende o melhor
  // PDF/print possível.
  //   forceCompact=true → vai direto pra compacto.
  //   senão → tenta normal; se não couber em 1 página, tenta compacto;
  //           se compacto também não couber, usa normal + paginação segura.
  // Retorna { canvas, blocks, domHeight, mode, pages } onde mode é
  // "normal-1p" | "compact-1p" | "normal-multi".
  const captureSmart = async (pagePxRef) => {
    if (forceCompact) {
      const compact = await captureCanvas({ compact: true });
      if (!compact) return null;
      const pages = compact.canvas.height > pagePxRef(compact) ? 2 : 1;
      return { ...compact, mode: "compact-1p", pages };
    }
    // 1) tenta normal
    const normal = await captureCanvas({ compact: false });
    if (!normal) return null;
    const normalPagePx = pagePxRef(normal);
    if (normal.canvas.height <= normalPagePx) {
      return { ...normal, mode: "normal-1p", pages: 1 };
    }
    // 2) não coube — tenta compacto
    const compact = await captureCanvas({ compact: true });
    if (compact && compact.canvas.height <= pagePxRef(compact)) {
      return { ...compact, mode: "compact-1p", pages: 1 };
    }
    // 3) ainda não cabe — usa normal com paginação segura (sem cortar blocos)
    if (compact && compact.canvas !== normal.canvas) releaseCanvas(compact.canvas);
    return { ...normal, mode: "normal-multi", pages: Math.ceil(normal.canvas.height / normalPagePx) };
  };

  // Imprime via iframe oculto (não abre popup, não imprime a página inteira).
  // Sistema inteligente 3-tier:
  //   1. Tenta normal → se cabe em 1 página, perfeito.
  //   2. Senão, tenta compacto → "Recibo otimizado para 1 página".
  //   3. Senão, normal + paginação segura em pontos entre blocos.
  const print = async () => {
    if (!ref.current) { toast.error("Aguarde o recibo carregar"); return; }
    setBusy(true);
    try {
      const A4_W_MM = 210, A4_H_MM = 297, MARGIN_MM = wideLayout ? 13 : 6;
      const usableWidthMm = A4_W_MM - MARGIN_MM * 2;
      const usableHeightMm = A4_H_MM - MARGIN_MM * 2;
      // pagePx em função do canvas (capture pode variar de altura)
      const pagePxOf = (cap) => Math.floor((cap.canvas.width / usableWidthMm) * usableHeightMm);
      const captured = await captureSmart(pagePxOf);
      if (!captured) { toast.error("Não foi possível gerar o recibo"); return; }
      const { canvas, blocks, domHeight, mode, pages } = captured;
      if (canvas.width < 50 || canvas.height < 50) {
        toast.error("Recibo ainda carregando — tente novamente");
        return;
      }

      const pxPerMm = canvas.width / usableWidthMm;
      const pagePx = Math.floor(usableHeightMm * pxPerMm);
      const domScale = canvas.height / domHeight;
      const plan = planPages(canvas.height, pagePx, blocks, domScale);

      // gera <img> por página (cada um com break-inside:avoid e quebra de página depois)
      const pageImgs = plan.map(({ startPx, endPx }) => {
        const sub = sliceCanvas(canvas, startPx, endPx);
        const heightMm = (endPx - startPx) / pxPerMm;
        return `<div class="page"><img src="${sub.toDataURL("image/png")}" style="width:${usableWidthMm}mm;height:${heightMm}mm;"/></div>`;
      }).join("");

      const iframe = document.createElement("iframe");
      iframe.style.cssText = "position:fixed;right:0;bottom:0;width:1px;height:1px;border:0;opacity:0;pointer-events:none;";
      document.body.appendChild(iframe);
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(`<!doctype html><html><head><title>Recibo ${orderNum}</title>
        <style>
          html,body{margin:0;padding:0;background:#fff;}
          @page { size: A4; margin: ${MARGIN_MM}mm; }
          .page { break-inside: avoid; page-break-inside: avoid; break-after: page; page-break-after: always; }
          .page:last-child { break-after: auto; page-break-after: auto; }
          .page img { display:block; }
          @media print { html,body{width:210mm;} .page img { width: ${usableWidthMm}mm; height:auto; } }
        </style></head>
        <body>${pageImgs}</body></html>`);
      doc.close();
      const cleanup = () => { try { document.body.removeChild(iframe); } catch {} };
      const triggerPrint = () => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) { /* ignore */ }
        setTimeout(cleanup, 1500);
      };
      const imgs = doc.querySelectorAll("img");
      let loaded = 0;
      if (imgs.length === 0) { triggerPrint(); return; }
      imgs.forEach(img => {
        if (img.complete) { loaded++; if (loaded === imgs.length) setTimeout(triggerPrint, 80); }
        else img.onload = img.onerror = () => { loaded++; if (loaded === imgs.length) setTimeout(triggerPrint, 80); };
      });
      // Feedback do modo escolhido
      if (mode === "compact-1p") toast.success("Recibo otimizado para 1 página");
      else if (mode === "normal-multi") toast.info(`Recibo grande — gerou ${plan.length} página(s) sem cortes`);
    } catch (e) {
      toast.error("Erro ao imprimir");
    } finally { setBusy(false); }
  };

  const isMobile = typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  // Insere zero-width space entre dígitos pra impedir que viewers de PDF
  // detectem telefone/CNPJ como link auto-clicável. Invisível ao usuário.
  const noAutoLink = (s) => String(s == null ? "" : s).replace(/(\d)(?=\d)/g, "$1\u200B");

  // Constrói o HTML autônomo do recibo. Usado tanto por printAsHtml (print
  // nativo) (o botao PDF usa downloadPDF/html2canvas).
  const buildReceiptHtml = () => {
    const c = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, ch => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]
    ));
    const showLogo = store?.receipt_show_logo !== false && store?.logo;
    const showAddress = store?.receipt_show_address !== false && store?.address;
    const showPhone = store?.receipt_show_phone !== false && store?.phone;
    const showCnpj = store?.receipt_show_cnpj !== false && store?.cnpj;
    const showCustomer = store?.receipt_show_customer !== false && sale.customer;
    const showTerms = store?.receipt_show_terms !== false && store?.receipt_terms;
    const dateStr = new Date(sale.created_at).toLocaleString("pt-BR");

    const itemsRows = (sale.items || []).map(it => {
      const lineGross = it.unit_price * it.quantity;
      const lineDisc = Math.max(0, Number(it.item_discount || 0));
      const lineTotal = Math.max(0, lineGross - lineDisc);
      const codes = Array.isArray(it.codes) && it.codes.length > 0 ? it.codes : (it.code ? [it.code] : []);
      const codeLine = codes.length ? `<div class="code">cód. ${c(codes.join(" · "))}</div>` : "";
      const discLine = lineDisc > 0 ? `<div class="mini">${brl(it.unit_price)} × ${it.quantity} <span style="color:#c0392b">- ${brl(lineDisc)} desc.</span></div>` : "";
      const subtCell = lineDisc > 0
        ? `<div class="strike">${brl(lineGross)}</div><div>${brl(lineTotal)}</div>`
        : brl(lineGross);
      return `<tr>
        <td>${c(cleanItemName(it.name))}${codeLine}${discLine}</td>
        <td class="c">${it.quantity}</td>
        <td class="r">${subtCell}</td>
      </tr>`;
    }).join("");

    const totalLineDiscounts = (sale.items || []).reduce((s, it) => s + Math.max(0, Number(it.item_discount || 0)), 0);
    const grossSubtotal = (sale.items || []).reduce((s, it) => s + it.unit_price * it.quantity, 0);
    const feesArr = Array.isArray(sale.fees) ? sale.fees : [];
    const feesTotal = feesArr.reduce((s, f) => s + Number(f.amount || 0), 0);
    const paymentsArr = Array.isArray(sale.payments) ? sale.payments.filter(p => p && p.method) : [];
    const grand = Number(sale.total || 0) + feesTotal;

    const feesBlock = feesArr.length > 0 ? `
      <div class="fees">
        <div class="tinyh">Taxas extras</div>
        ${feesArr.map(f => `<div class="row"><span>${c(f.label)}</span><span>+${brl(f.amount)}</span></div>`).join("")}
        <div class="row total"><span>Total final</span><span>${brl(grand)}</span></div>
      </div>` : "";

    const paymentBlock = paymentsArr.length > 1
      ? `<div class="pay">
           <div class="tinyh">Pagamento</div>
           ${paymentsArr.map(p => `<div class="row"><span>${c(p.method)}${p.note ? ` (${c(p.note)})` : ""}</span><span>${brl(p.amount)}</span></div>`).join("")}
         </div>`
      : `<div class="row muted"><span>Pagamento</span><span>${c(sale.payment_method === "Pendente programado" ? "Crediário" : sale.payment_method)}</span></div>`;

    const tradeInBlock = sale.trade_in ? `
      <div class="tradein">
        <div class="tinyh">Recebido na troca</div>
        <div>${c(sale.trade_in.product_name)}${sale.trade_in.brand ? ` · ${c(sale.trade_in.brand)}` : ""}</div>
        ${sale.trade_in.code ? `<div class="mini">Cód: ${c(sale.trade_in.code)}</div>` : ""}
        ${sale.trade_in.condition ? `<div class="mini">Estado: ${c(sale.trade_in.condition)}</div>` : ""}
        <div class="row"><span>Valor abatido</span><span>-${brl(sale.trade_in.value)}</span></div>
      </div>` : "";

    const plan = sale.installment_plan_full || sale.installment_plan;
    let installmentsBlock = "";
    if (plan && plan.installments && plan.installments.length) {
      const byAmount = {};
      for (const p of plan.installments) {
        const k = Number(p.amount).toFixed(2);
        byAmount[k] = (byAmount[k] || 0) + 1;
      }
      installmentsBlock = `
        <div class="installments">
          <div class="tinyh">Crediário</div>
          ${Object.entries(byAmount).map(([amt, qtd]) => `<div>${qtd}x de R$ ${amt.replace(".", ",")}</div>`).join("")}
          <div class="mini muted" style="margin-top:2mm">Vencimentos:</div>
          ${plan.installments.map(p => `<div class="row"><span>${new Date(p.due_date + "T12:00:00").toLocaleDateString("pt-BR")}</span><span>${brl(p.amount)}</span></div>`).join("")}
        </div>`;
    }

    const noteBlock = (sale.show_note_on_receipt && sale.note) ? `
      <div class="note">
        <div class="tinyh">Observação</div>
        <div>${c(sale.note)}</div>
      </div>` : "";

    const termsFontMap = { xxxs: "6.5pt", xxs: "7pt", xs: "7.5pt", sm: "9pt", md: "10pt", lg: "11.5pt" };
    const termsSize = termsFontMap[store?.receipt_terms_font_size || "xs"] || termsFontMap.xs;
    const termsBlock = showTerms ? `
      <div class="terms" style="font-size:${termsSize}">
        <div class="tinyh">Termos e Condições</div>
        <div class="terms-body">${c(store.receipt_terms)}</div>
      </div>` : "";

    // Números com zero-width space entre dígitos → viewers de PDF não detectam
    // como telefone/CNPJ → não viram links azuis clicáveis. Invisível.
    const phoneSafe = showPhone ? noAutoLink(store.phone) : "";
    const cnpjSafe = showCnpj ? noAutoLink(store.cnpj) : "";
    const custPhoneSafe = sale.customer?.phone ? noAutoLink(sale.customer.phone) : "";
    const custCpfSafe = sale.customer?.cpf ? noAutoLink(sale.customer.cpf) : "";

    const headerRight = `
      ${c(store?.name || "Loja")}
      ${showAddress ? `<div class="sub">${c(store.address)}</div>` : ""}
      ${phoneSafe ? `<div class="sub">${c(phoneSafe)}</div>` : ""}
      ${cnpjSafe ? `<div class="sub">CNPJ: ${c(cnpjSafe)}</div>` : ""}`;

    return `<!doctype html>
<html lang="pt-BR"><head>
<meta charset="utf-8"/>
<meta name="format-detection" content="telephone=no, address=no, email=no, date=no"/>
<meta name="x-apple-disable-message-reformatting"/>
<title>Recibo ${c(orderNum)}</title>
<style>
  @page { size: A4; margin: 13mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: #0A0A0A;
    font-family: Arial, Helvetica, sans-serif; font-size: 10.5pt; line-height: 1.45; }
  a { color: inherit !important; text-decoration: none !important; }
  .wrap { width: 100%; }
  .row { display: flex; justify-content: space-between; align-items: baseline; }
  .r { text-align: right; }
  .c { text-align: center; }
  .muted { color: #6b6b6b; }
  .mini { font-size: 8.5pt; color: #666; }
  .code { font-family: 'Courier New', monospace; font-size: 7.5pt; color: #888; }
  .strike { text-decoration: line-through; color: #999; font-size: 8.5pt; }
  .tinyh { font-size: 7.5pt; text-transform: uppercase; letter-spacing: 1.5px; color: #666; margin-bottom: 1mm; font-weight: 600; }
  .header { display: flex; justify-content: space-between; align-items: flex-start;
    border-bottom: 1px solid #E5E5E5; padding-bottom: 4mm; margin-bottom: 3mm; gap: 8mm; }
  .header img { width: 18mm; height: 18mm; object-fit: cover; border-radius: 2mm; }
  .header .r-side { text-align: right; font-size: 14pt; font-weight: 700; }
  .header .sub { font-size: 9pt; font-weight: 400; color: #666; margin-top: 1mm; }
  .meta { display: flex; justify-content: space-between; font-size: 9pt; color: #666; margin-bottom: 3mm; }
  .customer { margin-bottom: 3mm; font-size: 9.5pt; }
  .customer .name { font-weight: 600; margin-bottom: 0.5mm; }
  .customer .sub { color: #444; font-size: 9pt; }
  table { width: 100%; border-collapse: collapse; }
  thead th { text-align: left; padding: 1.5mm 1mm; border-bottom: 1px solid #E5E5E5;
    font-size: 8.5pt; text-transform: uppercase; letter-spacing: 0.5px; color: #444; }
  thead th.c { text-align: center; }
  thead th.r { text-align: right; }
  tbody td { padding: 1.5mm 1mm; border-bottom: 1px solid #f4f4f4; vertical-align: top; font-size: 10pt; }
  tbody td.c { text-align: center; }
  tbody td.r { text-align: right; }
  .totals { border-top: 1px solid #E5E5E5; margin-top: 3mm; padding-top: 3mm; }
  .totals .row { padding: 0.5mm 0; }
  .totals .grand { font-size: 13pt; font-weight: 700; margin-top: 1.5mm; }
  .fees, .pay, .installments, .tradein, .note, .terms {
    margin-top: 3mm; padding-top: 2mm; border-top: 1px dashed #E5E5E5; }
  .terms { border: 1px solid #E5E5E5; border-radius: 2mm; padding: 3mm; margin-top: 3mm; }
  .terms-body { white-space: pre-line; line-height: 1.35; }
  .tradein { border: 1px dashed #d4a54a; background: #fdf6e3; border-radius: 2mm; padding: 2mm 3mm; }
  .footer { text-align: center; margin-top: 5mm; font-size: 8.5pt; color: #999; }
  @media print { html, body { width: 100%; } }
</style>
</head><body>
<div class="wrap">
  <div class="header">
    ${showLogo ? `<img src="${c(store.logo)}" crossorigin="anonymous" alt=""/>` : `<div></div>`}
    <div class="r-side">${headerRight}</div>
  </div>
  <div class="meta">
    <span>Pedido #${c(orderNum)}</span>
    <span>${c(dateStr)}</span>
  </div>
  ${showCustomer ? `
    <div class="customer">
      <div class="tinyh">Cliente</div>
      <div class="name">${c(sale.customer.name)}</div>
      ${custCpfSafe ? `<div class="sub">CPF: ${c(custCpfSafe)}</div>` : ""}
      ${sale.customer.address ? `<div class="sub">Endereço: ${c(sale.customer.address)}</div>` : ""}
      ${custPhoneSafe ? `<div class="sub">${c(custPhoneSafe)}</div>` : ""}
    </div>` : ""}
  <table>
    <thead><tr><th>Item</th><th class="c">Qtd</th><th class="r">Subtotal</th></tr></thead>
    <tbody>${itemsRows}</tbody>
  </table>
  <div class="totals">
    ${totalLineDiscounts > 0 ? `
      <div class="row"><span>Subtotal (sem descontos)</span><span>${brl(grossSubtotal)}</span></div>
      <div class="row" style="color:#c0392b"><span>Descontos nos itens</span><span>-${brl(totalLineDiscounts)}</span></div>
    ` : ""}
    <div class="row"><span>Subtotal</span><span>${brl(sale.subtotal)}</span></div>
    ${sale.discount > 0 ? `<div class="row" style="color:#c0392b"><span>Desconto geral</span><span>-${brl(sale.discount)}</span></div>` : ""}
    ${tradeInBlock}
    <div class="row grand"><span>Total</span><span>${brl(sale.total)}</span></div>
    ${feesBlock}
    ${paymentBlock}
    ${installmentsBlock}
  </div>
  ${noteBlock}
  ${termsBlock}
  <div class="footer">Obrigado pela preferência!</div>
</div>
</body></html>`;
  };

  // ── Impressão via HTML NATIVO (mesma técnica do TradeInTerm.jsx) ────────
  // Usado APENAS quando wideLayout=true. Imprime texto vetorial nativo do
  // browser. Sem risco de "ExpertStoreBR" / letras coladas.
  const printAsHtml = () => {
    if (!ref.current) { toast.error("Aguarde o recibo carregar"); return; }
    try {
      // Injeta script de auto-print no HTML pra impressão
      const html = buildReceiptHtml().replace("</body>",
        `<script>window.onload=function(){setTimeout(function(){window.focus();window.print();},200);};window.onafterprint=function(){window.close();};</script></body>`);
      const w = window.open("", "_blank", "width=900,height=1000,menubar=no,toolbar=no,location=no,status=no");
      if (!w) {
        const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
        const w2 = window.open(url, "_blank");
        if (!w2) toast.error("Popups bloqueados. Habilite popups para imprimir.");
        return;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (e) {
      toast.error("Erro ao gerar recibo");
    }
  };



  const saveImage = async () => {
    setBusy(true);
    try {
      const canvas = await captureOnlyCanvas();
      const blob = await new Promise(res => canvas.toBlob(res, "image/png"));
      // On mobile try Web Share with file so user can "Save Image" to Photos/Gallery
      const file = new File([blob], `recibo-${orderNum}.png`, { type: "image/png" });
      if (isMobile && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: `Recibo #${orderNum}` });
          return;
        } catch (err) {
          // user cancelled or share failed — fall through to download
          if (err && err.name === "AbortError") return;
        }
      }
      // Desktop or fallback: trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = `recibo-${orderNum}.png`;
      link.href = url;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Imagem salva");
    } catch (e) {
      toast.error("Erro ao salvar imagem");
    } finally { setBusy(false); }
  };

  const downloadPDF = async () => {
    setBusy(true);
    try {
      const margin = 10;
      const pdfTmp = new jsPDF({ unit: "mm", format: "a4" });
      const pageWidthMm = pdfTmp.internal.pageSize.getWidth();
      const pageHeightMm = pdfTmp.internal.pageSize.getHeight();
      const maxWidth = pageWidthMm - margin * 2;
      const maxHeight = pageHeightMm - margin * 2;
      const pagePxOf = (cap) => Math.floor((cap.canvas.width / maxWidth) * maxHeight);
      const captured = await captureSmart(pagePxOf);
      if (!captured) { toast.error("Erro ao gerar PDF"); return; }
      const { canvas, blocks, domHeight, mode } = captured;

      // GUARDA 1 — canvas degenerado (recibo ainda carregando).
      if (canvas.width < 50 || canvas.height < 50) {
        toast.error("Recibo ainda carregando — tente novamente");
        return;
      }

      // GUARDA 2 — canvas zerado pelo iOS (teto de area/memoria). Sem esta
      // checagem o jsPDF salvaria N paginas totalmente em branco. Cai para o
      // caminho vetorial (mesmo motor do botao Imprimir), que nao usa canvas.
      if (isCanvasBlank(canvas)) {
        releaseCanvas(canvas);
        toast.info("Recibo grande para PDF neste aparelho — abrindo impressao: escolha \"Salvar em PDF\"");
        if (wideLayout) printAsHtml(); else await print();
        return;
      }

      const pdf = new jsPDF({ unit: "mm", format: "a4" });
      const pxPerMm = canvas.width / maxWidth;
      const pagePx = Math.floor(maxHeight * pxPerMm);
      const domScale = canvas.height / domHeight;

      // Corta o branco final para nao emitir paginas praticamente vazias.
      const contentHeight = trimTrailingWhite(canvas);

      const plan = planPages(contentHeight, pagePx, blocks, domScale);
      plan.forEach((slice, i) => {
        const sub = sliceCanvas(canvas, slice.startPx, slice.endPx);
        const sliceMm = sub.height / pxPerMm;
        if (i > 0) pdf.addPage();
        pdf.addImage(sub.toDataURL("image/png"), "PNG", margin, margin, maxWidth, sliceMm);
        releaseCanvas(sub); // libera cada fatia (memoria de canvas no iOS)
      });

      pdf.save(`recibo-${orderNum}.pdf`);
      if (mode === "compact-1p") toast.success("PDF otimizado em 1 página");
      else if (mode === "normal-multi") toast.info(`PDF com ${plan.length} página(s) — sem cortes`);
      else toast.success("PDF gerado");
    } catch (e) {
      toast.error("Erro ao gerar PDF");
    } finally { setBusy(false); }
  };

  const share = async () => {
    setBusy(true);
    try {
      const canvas = await captureOnlyCanvas();
      const blob = await new Promise(res => canvas.toBlob(res, "image/png"));
      const file = new File([blob], `recibo-${orderNum}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Recibo #${orderNum}`, text: `Total: ${brl(sale.total)}` });
      } else if (navigator.share) {
        await navigator.share({ title: `Recibo #${orderNum}`, text: `Total: ${brl(sale.total)}` });
      } else {
        saveImage();
      }
    } catch (e) {
      // user cancelled or error
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 no-print" data-testid="receipt-modal">
      <div className={`bg-white ${wideLayout ? "max-w-4xl" : "max-w-md"} w-full max-h-[90vh] overflow-y-auto rounded-md receipt-force-light`}>
        <div ref={ref} className={`receipt ${wideLayout ? "receipt-wide p-6" : "p-8"}`} style={{ background: "#FFFFFF", color: "#0A0A0A" }}>
          <div className="print-block flex items-start justify-between gap-3 border-b border-[#E5E5E5] pb-4 mb-4">
            {store?.receipt_show_logo !== false && store?.logo ? (
              <img src={store.logo} alt="logo" crossOrigin="anonymous" className="w-16 h-16 rounded object-cover shrink-0"/>
            ) : <div className="w-0"/>}
            <div className="text-right flex-1 min-w-0">
              <h2 className="font-display text-lg font-bold leading-tight break-words">{store?.name || "Loja"}</h2>
              {store?.receipt_show_address !== false && store?.address && <div className="text-xs text-neutral-500 break-words">{store.address}</div>}
              {store?.receipt_show_phone !== false && store?.phone && <div className="text-xs text-neutral-500">{store.phone}</div>}
              {store?.receipt_show_cnpj !== false && store?.cnpj && <div className="text-xs text-neutral-500">CNPJ: {store.cnpj}</div>}
            </div>
          </div>
          <div className="print-block text-xs text-neutral-500 flex justify-between mb-2">
            <span>Pedido #{orderNum}</span>
            <span>{new Date(sale.created_at).toLocaleString('pt-BR')}</span>
          </div>
          {store?.receipt_show_customer !== false && sale.customer && (
            <div className="print-block text-xs mb-3">
              <div className="font-medium">Cliente</div>
              <div>{sale.customer.name}</div>
              {sale.customer.cpf && <div className="text-neutral-700">CPF: {sale.customer.cpf}</div>}
              {sale.customer.address && <div className="text-neutral-700">Endereço: {sale.customer.address}</div>}
              {sale.customer.phone && <div className="text-neutral-500">{sale.customer.phone}</div>}
            </div>
          )}
          <table className="w-full text-xs">
            <thead className="border-b border-[#E5E5E5]">
              <tr><th className="text-left py-1">Item</th><th className="text-right">Qtd</th><th className="text-right">Subt</th></tr>
            </thead>
            <tbody>
              {sale.items.map((it, i) => {
                const lineGross = it.unit_price * it.quantity;
                const lineDisc = Math.max(0, Number(it.item_discount || 0));
                const lineTotal = Math.max(0, lineGross - lineDisc);
                return (
                  <tr key={i} className="print-block border-b border-neutral-100 align-top">
                    <td className="py-1">
                      <div>{cleanItemName(it.name)}</div>
                      {(() => {
                        const codes = Array.isArray(it.codes) && it.codes.length > 0 ? it.codes : (it.code ? [it.code] : []);
                        if (codes.length === 0) return null;
                        return <div className="text-[9px] text-neutral-400 font-mono">cód. {codes.join(" · ")}</div>;
                      })()}
                      {lineDisc > 0 && (
                        <div className="text-[10px] text-neutral-500">
                          {brl(it.unit_price)} × {it.quantity}
                          <span className="text-red-600 ml-1">- {brl(lineDisc)} desc.</span>
                        </div>
                      )}
                    </td>
                    <td className="text-right py-1">{it.quantity}</td>
                    <td className="text-right py-1">
                      {lineDisc > 0 ? (
                        <>
                          <div className="text-neutral-400 line-through text-[10px]">{brl(lineGross)}</div>
                          <div>{brl(lineTotal)}</div>
                        </>
                      ) : brl(lineGross)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {(() => {
            const totalLineDiscounts = (sale.items || []).reduce((s, it) => s + Math.max(0, Number(it.item_discount || 0)), 0);
            const grossSubtotal = (sale.items || []).reduce((s, it) => s + it.unit_price * it.quantity, 0);
            const feesArr = Array.isArray(sale.fees) ? sale.fees : [];
            const feesTotal = feesArr.reduce((s, f) => s + Number(f.amount || 0), 0);
            const paymentsArr = Array.isArray(sale.payments) ? sale.payments.filter(p => p && p.method) : [];
            const grand = Number(sale.total || 0) + feesTotal;
            return (
              <div className="border-t border-[#E5E5E5] mt-3 pt-3 space-y-1 text-xs">
                {totalLineDiscounts > 0 && (
                  <>
                    <div className="flex justify-between"><span>Subtotal (sem descontos)</span><span>{brl(grossSubtotal)}</span></div>
                    <div className="flex justify-between text-red-700"><span>Descontos nos itens</span><span>-{brl(totalLineDiscounts)}</span></div>
                  </>
                )}
                <div className="flex justify-between"><span>Subtotal</span><span>{brl(sale.subtotal)}</span></div>
                {sale.discount > 0 && <div className="flex justify-between text-red-700"><span>Desconto geral</span><span>-{brl(sale.discount)}</span></div>}
                {sale.trade_in && (
                  <div className="print-block mt-2 border border-dashed border-amber-300 bg-amber-50 rounded p-2 text-[11px] text-amber-900">
                    <div className="font-medium uppercase text-[9px] tracking-widest text-amber-700">Recebido na troca</div>
                    <div>{sale.trade_in.product_name}{sale.trade_in.brand ? ` · ${sale.trade_in.brand}` : ""}</div>
                    {sale.trade_in.code && <div className="text-[10px] text-amber-700">Cód: {sale.trade_in.code}</div>}
                    {sale.trade_in.condition && <div className="text-[10px]">Estado: {sale.trade_in.condition}</div>}
                    <div className="flex justify-between mt-1 font-medium"><span>Valor abatido</span><span>-{brl(sale.trade_in.value)}</span></div>
                  </div>
                )}
                <div className="print-block flex justify-between font-display text-base"><span>Total</span><span>{brl(sale.total)}</span></div>
                {feesArr.length > 0 && (
                  <div className="print-block">
                    <div className="mt-1.5 pt-1.5 border-t border-dashed border-[#E5E5E5]">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-0.5">Taxas extras</div>
                      {feesArr.map((f, i) => (
                        <div key={i} className="flex justify-between text-[11px]"><span>{f.label}</span><span>+{brl(f.amount)}</span></div>
                      ))}
                    </div>
                    <div className="flex justify-between font-display text-base mt-1 border-t border-[#E5E5E5] pt-1">
                      <span>Total final</span><span>{brl(grand)}</span>
                    </div>
                  </div>
                )}
                {paymentsArr.length > 1 ? (
                  <div className="print-block mt-1.5 pt-1.5 border-t border-[#E5E5E5]">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-0.5">Pagamento</div>
                    {paymentsArr.map((p, i) => (
                      <div key={i} className="flex justify-between text-[11px]"><span>{p.method}{p.note ? ` (${p.note})` : ""}</span><span>{brl(p.amount)}</span></div>
                    ))}
                  </div>
                ) : (
                  <div className="print-block flex justify-between text-neutral-500"><span>Pagamento</span><span>{sale.payment_method === "Pendente programado" ? "Crediário" : sale.payment_method}</span></div>
                )}
                {/* ── Pendente Programado / Crediário ─────────────────── */}
                {(() => {
                  const plan = sale.installment_plan_full || sale.installment_plan; // backend pode anexar full
                  if (!plan || !plan.installments?.length) return null;
                  // Agrupa por valor (recibo: "3x R$200")
                  const byAmount = {};
                  for (const p of plan.installments) {
                    const k = Number(p.amount).toFixed(2);
                    byAmount[k] = (byAmount[k] || 0) + 1;
                  }
                  return (
                    <div className="print-block mt-2 border-t border-dashed border-[#E5E5E5] pt-1.5" data-testid="receipt-installments">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-0.5">Crediário</div>
                      {Object.entries(byAmount).map(([amt, qtd]) => (
                        <div key={amt} className="text-[11px]">{qtd}x de R$ {amt.replace(".", ",")}</div>
                      ))}
                      <div className="text-[10px] mt-1 text-neutral-500">Vencimentos:</div>
                      {plan.installments.map((p, i) => (
                        <div key={i} className="print-block text-[11px] flex justify-between">
                          <span>{new Date(p.due_date + "T12:00:00").toLocaleDateString("pt-BR")}</span>
                          <span>{brl(p.amount)}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            );
          })()}
          {sale.show_note_on_receipt && sale.note && (
            <div className="print-block mt-4 text-xs text-neutral-600 border-t border-[#E5E5E5] pt-3">
              <div className="font-medium">Observação</div>
              <div>{sale.note}</div>
            </div>
          )}
          {store?.receipt_show_terms !== false && store?.receipt_terms && (() => {
            const sizeMap = { xxxs: "text-[6px]", xxs: "text-[8px]", xs: "text-[9px]", sm: "text-[11px]", md: "text-[13px]", lg: "text-[15px]" };
            const sz = sizeMap[store.receipt_terms_font_size || "xs"] || sizeMap.xs;
            return (
              <div className={`print-block mt-4 ${sz} leading-snug text-neutral-700 border border-[#E5E5E5] rounded p-2 whitespace-pre-line`}>
                <div className="font-medium mb-1 text-[10px] uppercase tracking-wider">Termos e Condições</div>
                {store.receipt_terms}
              </div>
            );
          })()}
          <div className="print-block text-center text-[10px] text-neutral-400 mt-6">Obrigado pela preferência!</div>
        </div>
        <div className="px-4 pt-3 pb-1 border-t border-[#E5E5E5] no-print">
          <label className="flex items-center justify-between gap-2 text-xs cursor-pointer select-none rounded-md border border-dashed border-[#E5E5E5] hover:bg-neutral-50 px-3 py-2" data-testid="compact-mode-toggle">
            <span className="flex items-center gap-2">
              <Minimize2 size={14} className={forceCompact ? "text-emerald-600" : "text-neutral-500"}/>
              <span className="font-medium">Modo compacto</span>
              <span className="text-neutral-500 hidden sm:inline">— força otimização em 1 página</span>
            </span>
            <input
              type="checkbox"
              checked={forceCompact}
              onChange={(e)=>setForceCompact(e.target.checked)}
              className="h-4 w-4 accent-emerald-600 cursor-pointer"
              data-testid="compact-mode-checkbox"
            />
          </label>
          <p className="text-[10px] text-neutral-500 mt-1.5 px-1">
            Por padrão o sistema imprime normal. Se o recibo for grande, otimiza automaticamente. Ative para forçar a versão compacta.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 p-4 no-print">
          <Button variant="outline" onClick={onClose} data-testid="receipt-close"><X size={14} className="mr-1"/> Fechar</Button>
          <Button onClick={wideLayout ? printAsHtml : print} variant="outline" data-testid="receipt-print"><Printer size={14} className="mr-1"/> Imprimir</Button>
          <Button onClick={()=>setThermalOpen(true)} variant="outline" className="col-span-2 border-dashed" data-testid="receipt-print-thermal"><ReceiptIcon size={14} className="mr-1"/> Imprimir Cupom (térmica)</Button>
          <Button onClick={saveImage} disabled={busy} variant="outline" data-testid="receipt-image"><ImageDown size={14} className="mr-1"/> Salvar foto</Button>
          <Button onClick={downloadPDF} disabled={busy} variant="outline" data-testid="receipt-pdf"><FileDown size={14} className="mr-1"/> PDF</Button>
          <Button onClick={share} disabled={busy} className="col-span-2 bg-[#0A0A0A] hover:bg-black text-white" data-testid="receipt-share"><Share2 size={14} className="mr-1"/> Compartilhar</Button>
        </div>
      </div>
      {thermalOpen && (
        <ThermalReceipt data={sale} type="sale" store={store} onClose={()=>setThermalOpen(false)}/>
      )}
    </div>
  );
}
